import { useMemo, useEffect, useRef } from 'react';
import { FormikErrors } from 'formik';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';

import { Chart } from '../types';
import { useHelmChartValues } from '../helmChartSourceQueries/useHelmChartValues';
import { RegistryValue } from '../components/HelmRegistrySelect';
import { useHelmRepoVersions } from '../helmChartSourceQueries/useHelmRepoVersions';
import { HelmValuesInput } from '../components/HelmValuesInput';
import { HelmVersionSelect } from '../components/HelmVersionSelect';

import { HelmInstallFormValues } from './types';

type Props = {
  selectedChart: Chart;
  selectedRegistry: RegistryValue | null;
  version: string;
  values: string;
  errors: FormikErrors<HelmInstallFormValues>;
  onVersionChange: (version: string | undefined) => void;
  onValuesChange: (values: string) => void;
};

export function InstallOptionsFormSection({
  selectedChart,
  selectedRegistry,
  version,
  values,
  errors,
  onVersionChange,
  onValuesChange,
}: Props) {
  const environmentId = useEnvironmentId();
  const versionOptionsQuery = useHelmRepoVersions(
    {
      staleTime: 60 * 60 * 1000, // 1 hour cache
      select: (versions) =>
        versions.map((version, index) => ({
          label: index === 0 ? `${version.version} (latest)` : version.version,
          value: version,
        })),
    },
    {
      chartPath: selectedChart.name,
      repoURL: selectedRegistry?.registryId ? undefined : selectedChart.repo,
      registryID: selectedRegistry?.registryId,
    }
  );

  const prevOptionsLengthRef = useRef(versionOptionsQuery.data?.length ?? 0);

  useEffect(() => {
    const currentLength = versionOptionsQuery.data?.length ?? 0;
    const prevLength = prevOptionsLengthRef.current;

    // Set version when options become available (from undefined/empty to having options)
    if (prevLength === 0 && currentLength > 0) {
      const latestVersion = versionOptionsQuery.data?.[0]?.value?.version;
      if (latestVersion) {
        onVersionChange(latestVersion);
      }
    }

    prevOptionsLengthRef.current = currentLength;
  }, [versionOptionsQuery.data, onVersionChange]);

  const selectedVersion = useMemo(
    () =>
      versionOptionsQuery.data?.find((v) => v?.value?.version === version)
        ?.value ??
      versionOptionsQuery.data?.[0]?.value ??
      null,
    [versionOptionsQuery.data, version]
  );

  const repoParams = selectedRegistry?.isOCI
    ? {
        registryId: selectedRegistry.registryId,
      }
    : {
        repo: selectedChart.repo,
      };

  const isLatestVersionFetched =
    !versionOptionsQuery.data?.length ||
    (selectedVersion?.version ===
      versionOptionsQuery.data?.[0]?.value?.version &&
      selectedVersion?.repo === versionOptionsQuery.data?.[0]?.value?.repo);

  const chartValuesRefQuery = useHelmChartValues(
    {
      chart: selectedChart.name,
      version,
      environmentId,
      ...repoParams,
    },
    isLatestVersionFetched
  );

  return (
    <FormSection title="Install options" className="mt-4">
      <FormControl
        label="Version"
        inputId="version-input"
        errors={errors.version}
      >
        <HelmVersionSelect
          value={selectedVersion}
          options={versionOptionsQuery.data ?? []}
          onChange={(version) => {
            onVersionChange(version?.version);
          }}
          data-cy="helm-version-input"
          isLoading={versionOptionsQuery.isInitialLoading}
        />
      </FormControl>
      <HelmValuesInput
        values={values}
        setValues={onValuesChange}
        valuesRef={chartValuesRefQuery.data?.values ?? ''}
        isValuesRefLoading={chartValuesRefQuery.isInitialLoading}
      />
    </FormSection>
  );
}
